<script>
    import { onMount } from "svelte";
    import { hash } from "../lib/util"

    /** @import { CalendarEvent } from "./types.js" */


    const colorPalette = (()=>{
        const totalColors = 24;
        const colors = [];
        for (let i=0; i < totalColors; i++) {
            colors.push(`oklch(from var(--blue) l calc(c + .05) ${360 / (totalColors) * (Math.floor(i))}deg)`);
        }

        return colors;
    })();
    /** @type {Object.<string, string>} */
    let colorCache = {};

    /**
    @param {string} id 
    @param {Holiday} ev
    @returns {string}
    */
    function defaultColorFilter(id, ev) {
        return colorPalette[Math.abs(hash(id) + ev.startDate) % colorPalette.length];
    };


    /** @type {{
        onscroll: (startDate: number, endDate: number) => void
        displayFilter: (event: CalendarEvent) => boolean, 
        hashedFilter:  (event: CalendarEvent) => boolean,
        colorFilter:   (id: str, event: CalendarEvent) => string,
    }} */
    let {
        onscroll  = (_, __) => {},
        displayFilter = _ => true,
        hashedFilter  = _ => false,
        colorFilter   = defaultColorFilter
    } = $props();


    /** @type {HTMLTableSectionElement} */
    let calendar = $state();
    /** @type {HTMLTableSectionElement} */
    let months = $state();

    /** @type {HTMLDialogElement} */
    let gotoDialog = $state();
    /** @type {{year: number, month: number}} */
    let gotoDate = $state((()=>{
        const today = new Date(Date.now());
        return { year: today.getFullYear(), month: today.getMonth()+1 };
    })());

    const markerElem = document.createElement("div");
    markerElem.classList.add("marker");
    markerElem.textContent = "󰩷";

    /** @type {string[]} */
    let selectedEvents = $state([]);
    /** @type {Date?} */
    let selectedDay = $state();

    export const getSelectedEvents = () => selectedEvents;
    export const getSelectedDay = () => selectedDay;
    export const getEventColor = id => colorCache[id];
    export const clearSelected = () => {
        selectedEvents = [];
        selectedDay = undefined;
        markerElem.remove();
    };

    const ms_in_day = 24 * 3600 * 1000;
    const ms_in_week = 7 * ms_in_day;
    const loaded_range_weeks = 64;
    const load_chunk_size_weeks = 32;
    let loadedCalendar_dateStart = 0;
    let scroll_lock = undefined;


    /** @param {number} timestamp @param {string} [time="12:00"] @returns {number} */
    function normaliseTime(timestamp, time = "12:00") {
        const date = new Date(timestamp);
        return new Date(`${date.getFullYear()}-${date.getMonth()+1}-${date.getDate()} ${time}`).getTime();
    }

    function recomputeMonths() {
        const fragment = document.createDocumentFragment();
        let previousMonth = 99;
        let currentWeek = calendar.firstChild;

        while (currentWeek) {
            [currentWeek.firstChild, currentWeek.lastChild].forEach(d => {
                if (d.dataset.month != previousMonth) {
                    const date = new Date(d.dataset.date);
                    fragment.appendChild(document.createElement("div"));
                    fragment.lastChild.textContent = date.toLocaleString("fr", { month: "long", year: "numeric" });
                    if (date.getMonth() & 1) fragment.lastChild.classList.add("odd");
                    previousMonth = d.dataset.month;
                }

                fragment.lastChild.style.setProperty("--height", Number(fragment.lastChild.style.getPropertyValue("--height")) + 1);
            });

            currentWeek = currentWeek.nextSibling;
        }

        months.replaceChildren(fragment);
    }

    /**
        @param {number} startDate
        @param {number} numberWeeks
        @returns {DocumentFragment}
    */
    function generateWeeks(startDate, numberWeeks) {
        const fragment = document.createDocumentFragment();
        const today = new Date(Date.now()).toDateString();
        let startDateMonday = startDate;

        // Finding previous monday
        while (new Date(startDateMonday).getDay() != 1)
            startDateMonday -= ms_in_day;

        for (let week_counter = 0; week_counter < numberWeeks; week_counter++) {
            const weekElem = document.createElement("tr");

            for (let day_counter = 0; day_counter < 7; day_counter++) {
                /** @type {HTMLTableCellElement} */
                const dayElem = document.createElement("td");
                const date = new Date(normaliseTime(startDateMonday + (week_counter * 7 + day_counter) * ms_in_day));
                dayElem.dataset.date = date.toDateString();
                dayElem.dataset.month = date.getMonth();

                const dateElem = document.createElement("div");
                dateElem.textContent = date.getDate();
                dateElem.classList.add("date");
                dayElem.appendChild(dateElem);

                dayElem.onclick = e => {
                    /** @type {NodeList} */
                    const eventElems = e.target?.querySelectorAll(".event");
                    clearSelected();
                    for (const ev of eventElems)
                        selectedEvents.push(ev.dataset.eventId);
                    if (selectedEvents.length) {
                        e.target.appendChild(markerElem);
                        selectedDay = new Date(e.target.dataset.date);
                    }
                };

                // Colouring day and adding the "today" element
                if (date.getMonth() & 1) dayElem.classList.add("odd");
                if (date.toDateString() == today) {
                    const todayElem = document.createElement("div");
                    todayElem.classList.add("today");
                    dayElem.appendChild(todayElem);
                }

                weekElem.appendChild(dayElem);
            }

            fragment.appendChild(weekElem);
        }

        return fragment;
    }

    /** @param {Object.<string, CalendarEvent>} events */
    export function populateCalendarEvents(events) {
        calendar.querySelectorAll(".event, .phantom").forEach(e => e.remove());
        colorCache = {};
        clearSelected();

        const today = Date.now() / 1000;

        for (const weekElem of calendar.children) {
            /** @type Object.<string, number> */
            const weekEventLevelTracker = {};
            let weekMaxEventInDay = 0;

            for (const dayElem of weekElem.children) {
                const date = new Date(`${dayElem.dataset.date} 12:00`);

                // Adding any events present that day
                const ts = date.getTime();
                const days_events = Object.entries(events).filter( e => {
                    const start = normaliseTime(e[1].startDate * 1000, "00:00");
                    const end = normaliseTime(e[1].endDate * 1000, "23:59");
                    return displayFilter(e[1]) && (ts >= start) && (ts <= end);
                });
                weekMaxEventInDay = Math.max(weekMaxEventInDay, days_events.length);

                for (let i = 0; i < weekMaxEventInDay; i++) {
                    const phantomElem = document.createElement("div");
                    phantomElem.classList.add("phantom");
                    dayElem.appendChild(phantomElem);
                }

                for (let i = 0; i < days_events.length; i++) {
                    const [id, ev] = days_events[i];
                    const eventElem = document.createElement("div");
                    eventElem.classList.add("event");
                    if (hashedFilter(ev)) eventElem.classList.add("hashed");
                    if (date.toDateString() == new Date(ev.startDate * 1000).toDateString()){
                        eventElem.classList.add("first");
                        eventElem.tabIndex = 20000 + (today - ev.startDate) / 10000;
                        eventElem.onfocus = e => {
                            e.target?.parentNode?.dispatchEvent(new Event("click"));
                        };
                    }
                    if (date.toDateString() == new Date(ev.endDate * 1000).toDateString()) eventElem.classList.add("last");
                    const eventColor = colorFilter(id, ev);
                    eventElem.style.setProperty("--bg-color", eventColor);
                    eventElem.dataset.eventId = id;
                    colorCache[id] = eventColor;

                    if (weekEventLevelTracker[id] == undefined) {
                        const firstPhantomElem = dayElem.querySelector(".phantom");
                        const index = [...dayElem.querySelectorAll(".phantom, .event")].indexOf(firstPhantomElem);
                        weekEventLevelTracker[id] = index;
                        firstPhantomElem.replaceWith(eventElem);
                    } else {
                        [...dayElem.querySelectorAll(".phantom, .event")][weekEventLevelTracker[id]].replaceWith(eventElem);
                    }
                }
            }

            // Making sure all days in a week are equally divided between events
            for (const dayElem of weekElem.children) {
                for (let i = Math.max(0, weekMaxEventInDay - dayElem.querySelectorAll(".event, .phantom").length); i>0; i--) {
                    const phantomElem = document.createElement("div");
                    phantomElem.classList.add("phantom");
                    dayElem.appendChild(phantomElem);
                }
            }
        }
    }


    /**
        @param {HTMLCollection} children 
        @param {number} startIndex 
        @param {number} amount 
    */
    function trimChildren(children, startIndex, amount) {
        let index = 0;
        let currentChild = children[startIndex];
        while (currentChild && index < amount) {
            const nextChild = currentChild.nextSibling;
            currentChild.remove();
            currentChild = nextChild;
            index++;
        }
    }

    /** @param {number} startDate  */
    function generateCalendar(startDate) {
        loadedCalendar_dateStart = startDate - loaded_range_weeks * ms_in_week / 2;

        const fragment = generateWeeks(loadedCalendar_dateStart, loaded_range_weeks);

        const viewStartDate = new Date(startDate - 3 * ms_in_week).toDateString();
        const viewStartElem = (()=>{
            for (const week of [...fragment.children])
                for (const day of [...week.children])
                    if (day.dataset.date == viewStartDate) return day;
        })();

        calendar.replaceChildren(fragment);
        viewStartElem?.scrollIntoView({
            container: "nearest",
            block: "start"
        });
        recomputeMonths();
        onscroll(loadedCalendar_dateStart, loadedCalendar_dateStart + loaded_range_weeks * ms_in_week);
    }

    /** @param {Event} event  */
    function calendarScroll(event) {
        if (scroll_lock) return;
        scroll_lock = setTimeout(() => { scroll_lock = undefined; }, 50);

        const scrolltopmax = event.target.scrollHeight - event.target.clientHeight;
        const scrollRatio = event.target.scrollTop / scrolltopmax;
        const scrollOffset = event.target.scrollHeight * (load_chunk_size_weeks / loaded_range_weeks);


        if (scrollRatio < 0.2) {
            loadedCalendar_dateStart -= load_chunk_size_weeks * ms_in_week;
            calendar.prepend(generateWeeks(
                loadedCalendar_dateStart,
                load_chunk_size_weeks
            ));
            trimChildren(
                calendar.children,
                loaded_range_weeks,
                load_chunk_size_weeks
            );
            recomputeMonths();
            clearSelected();
            onscroll(loadedCalendar_dateStart, loadedCalendar_dateStart + loaded_range_weeks * ms_in_week);
            event.target.scroll({top: event.target.scrollTop + scrollOffset, behavior: "instant"});
            return;
        }

        if (scrollRatio > 0.8) {
            loadedCalendar_dateStart += load_chunk_size_weeks * ms_in_week;
            calendar.append(generateWeeks(
                loadedCalendar_dateStart + (loaded_range_weeks - load_chunk_size_weeks) * ms_in_week,
                load_chunk_size_weeks
            ));
            trimChildren(
                calendar.children,
                0,
                load_chunk_size_weeks
            );
            clearSelected();
            recomputeMonths();
            onscroll(loadedCalendar_dateStart, loadedCalendar_dateStart + loaded_range_weeks * ms_in_week);
            event.target.scroll({top: event.target.scrollTop - scrollOffset, behavior: "instant"});
            return;
        }
    }

    /** @param {number} date  */
    function calendarGoto(date) {
        generateCalendar(normaliseTime(date));
        gotoDialog.close();
    }

    onMount(()=>{
        generateCalendar(normaliseTime(Date.now()));
    });
</script>

<!-- <div style="display: flex; flex-direction: column;"> -->
<!--     {#each colorPalette as color} -->
<!--         <div style="display: inline-block; width: 2em; height: 2em; margin: .2em; background: {color};"></div> -->
<!--     {/each} -->
<!-- </div> -->

<div class="calendar-container" onscroll={calendarScroll}>
    <section class="calendar-header">
        <button onclick={()=>{gotoDialog.showModal()}}>󱙬</button>
        <div>L</div>
        <div>M</div>
        <div>M</div>
        <div>J</div>
        <div>V</div>
        <div>S</div>
        <div>D</div>
        <span></span>
    </section>

    <section class="calendar-body">
        <div class="months-display" bind:this={months}></div>
        <table class="weeks-table">
            <tbody bind:this={calendar}></tbody>
        </table>
    </section>
</div>

<dialog bind:this={gotoDialog} class="gradient" closedby="any">
    <h2>Aller À</h2>
    <div class="spread-buttons">
        <label>
            Année :
            <input
                type="number"
                bind:value={gotoDate.year}
                onfocusin={e=>{e.target.select();}}
                placeholder="Année"
                style="width: 6em;"
                onkeypress={e=>{if (e.key == "Enter") calendarGoto(new Date(`${gotoDate.year}-${gotoDate.month}-01 06:00`).getTime());}}  
            />
        </label>
        <label>
            Mois :
            <input
                type="number"
                bind:value={gotoDate.month}
                onfocusin={e=>{e.target.select();}}
                max="12"
                min="1"
                placeholder="Mois"
                style="width: 6em;"
                onkeypress={e=>{if (e.key == "Enter") calendarGoto(new Date(`${gotoDate.year}-${gotoDate.month}-01 06:00`).getTime());}}
            />
        </label>
    </div>
    <div class="spread-buttons">
        <button
            class="dark-button"
            style="color: var(--blue);"
            onclick={()=>{ generateCalendar(normaliseTime(Date.now())); gotoDialog.close(); }}
        >Aujourd'hui</button>
        <div style="flex-grow: 5;"></div>
        <button
            class="dark-button"
            onclick={()=>{calendarGoto(new Date(`${gotoDate.year}-${gotoDate.month}-01 06:00`).getTime());}}  
        >Go</button>
        <button
            class="dark-button"
            onclick={()=>{gotoDialog.close()}}
        >Fermer</button>
    </div>
</dialog>


<style>
.spread-buttons {
    display: flex;

    & > * { margin: auto .3em; }
    & label {
        font-weight: bold;
        font-size: 110%;
    }
}
</style>
